import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  DataTable,
  TableContainer,
  Table,
  TableRow,
  TableHeader,
  TableHead,
  TableBody,
  TableCell,
  Pagination,
  Tag,
  TableToolbar,
  Button,
  DataTableSkeleton,
} from '@carbon/react';
import { Add, Edit, TrashCan } from '@carbon/react/icons';

import { ApplicationData, PolicyData } from '../../models/master';
import {
  DataTableRow,
  DataTableHeader,
  DataTableCell,
} from '../../models/dataTable';
import dates from '../../lib/dates';
import images from '../../images/images';

import GenericTruncateString from '../GenericTruncateString/GenericTruncateString';
import { VerticalEmptyState } from '../EmptyState/EmptyState';

import './AssociatedPoliciesTable.scss';

const AssociatedPoliciesTable = (props: {
  headers: { header: string; key: string }[];
  rows: PolicyData[];
  applicationData?: ApplicationData | null;
  loading: boolean;
  openCreatePolicy: () => void;
  openEditPolicy: (id: string) => void;
  requestDelete: (id: string) => void;
}) => {
  const {
    headers,
    rows,
    applicationData,
    loading,
    openCreatePolicy,
    openEditPolicy,
    requestDelete,
  } = props;
  const { t } = useTranslation('applicationDetails');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const handlePagination = (change: any) => {
    setPage(change.page);
    setPageSize(change.pageSize);
  };

  if (loading) {
    return (
      <DataTableSkeleton
        rowCount={3}
        headers={headers}
        showHeader
        showToolbar
      />
    );
  }
  return (
    <div className='associated-policies-table'>
      <DataTable rows={rows} headers={headers}>
        {({
          rows,
          headers,
          getHeaderProps,
          getRowProps,
          getTableProps,
          getToolbarProps,
          getTableContainerProps,
        }: {
          rows: DataTableRow[];
          headers: DataTableHeader[];
          getTableProps: () => any;
          getToolbarProps: any;
          getHeaderProps: any;
          getRowProps: any;
          getTableContainerProps: any;
        }) => (
          <TableContainer {...getTableContainerProps()}>
            <TableToolbar {...getToolbarProps()}>
              <div className='table-toolbar'>
                {t('policies.header.intro', {
                  name: applicationData?.name,
                })}

                {Array.isArray(rows) && rows.length > 0 && (
                  <Button
                    kind='ghost'
                    className='create-policy-button'
                    renderIcon={Add}
                    onClick={openCreatePolicy}
                  >
                    {t('policies.header.addButtonText')}
                  </Button>
                )}
              </div>
            </TableToolbar>
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {headers.map((header: DataTableHeader) => (
                    <TableHeader
                      key={header.key}
                      {...getHeaderProps({
                        header,
                      })}
                    >
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              {rows.length > 0 ? (
                <TableBody>
                  {rows.map((row: DataTableRow) => (
                    <TableRow {...getRowProps({ row })} key={row.id}>
                      {row.cells.map((cell: DataTableCell) => {
                        if (cell.info.header === 'name') {
                          return (
                            <TableCell key={cell.id}>
                              <Link
                                className='no-underline'
                                to={`/connectionaccesspolicydetails?policyId=${row.id}`}
                              >
                                <GenericTruncateString
                                  str={cell.value}
                                ></GenericTruncateString>
                              </Link>
                            </TableCell>
                          );
                        } else if (cell.info.header === 'to_svc_name') {
                          return (
                            <TableCell key={cell.id}>
                              <div className='to-svc-cell'>
                                <span className='icon'>
                                  {images.serviceIconSmall()}
                                </span>
                                {cell.value}
                              </div>
                            </TableCell>
                          );
                        } else if (cell.info.header === 'labels') {
                          return (
                            <TableCell key={cell.id}>
                              <div className='labels-cell'>
                                {cell.value.length === 0 && '—'}
                                {cell.value
                                  ?.slice(0, 2)
                                  .map((label: string) => (
                                    <Tag color={'green'}>{label}</Tag>
                                  ))}
                                {cell.value.length > 2 && (
                                  <Tag color='grey'>
                                    {`+${cell.value.length - 2}`}
                                  </Tag>
                                )}
                              </div>
                            </TableCell>
                          );
                        } else if (cell.info.header === 'delete') {
                          return (
                            <TableCell key={cell.id}>
                              <div className='action-icons'>
                                <Button
                                  kind='ghost'
                                  hasIconOnly
                                  iconDescription={t('policies.editTooltip')}
                                  renderIcon={Edit}
                                  onClick={() => openEditPolicy(row.id)}
                                />
                                <Button
                                  kind='ghost'
                                  hasIconOnly
                                  iconDescription={t('policies.delete.tooltip')}
                                  renderIcon={TrashCan}
                                  onClick={() => requestDelete(row.id)}
                                />
                              </div>
                            </TableCell>
                          );
                        } else {
                          return (
                            <TableCell key={cell.id}>
                              {dates.getUserFriendlyDate(cell.value)}
                            </TableCell>
                          );
                        }
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              ) : (
                <VerticalEmptyState
                  icon={images.noPoliciesIcon()}
                  header={t('policies.emptyState.header')}
                  description={
                    <Trans
                      i18nKey={t('policies.emptyState.description', {
                        name: applicationData?.name,
                      })}
                    />
                  }
                  link={true}
                  buttonText={t('policies.createPolicy')}
                  click={openCreatePolicy}
                />
              )}
            </Table>
            {rows.length > 10 && (
              <Pagination
                totalItems={rows.length}
                itemsPerPageText={t('deployments.pagination.itemsPerPage')}
                itemRangeText={(
                  min: number,
                  max: number,
                  totalLength: number
                ) => {
                  return t('deployments.pagination.itemKey', {
                    min: min,
                    max: max,
                    total: totalLength,
                  });
                }}
                pageSize={pageSize}
                page={page}
                pageRangeText={(current: number, total: number) =>
                  t('deployments.pagination.pageKey', { sum: total })
                }
                pageSizes={[10, 25, 50, 100, 250]}
                onChange={(change: any) => handlePagination(change)}
              />
            )}
          </TableContainer>
        )}
      </DataTable>
    </div>
  );
};

export default AssociatedPoliciesTable;
